import * as React from 'react'
import { ComponentProps, JSX } from 'react'
import { useTranslation } from 'react-i18next'

import { useFiltersApi } from '../../../hooks'

import { FilterProps } from '../../common/Filter/types'
import { SelectFilter } from '../../common/Filter/SelectFilter/SelectFilter'
import { type Option } from '../../common/Form/types'

import { useFilterLabels } from './useFilterLabels'
import { GetTagsRequestParams } from '@/hooks/useApi/useFiltersApi/types'
import { type Tag } from '@/hooks/useApi/types'

export type TagFilterProps = {
    name: string
    params: Partial<GetTagsRequestParams>
    onChange: (tag: string[]) => void
    value: string[]
    defaultValue: string[]
    disabled?: boolean
    valueLabel?: string
    className?: string
} & Pick<FilterProps, 'containerRef'>

export const TagFilter: React.FC<TagFilterProps> = (props) => {
    const { disabled, value, defaultValue, name, params, onChange } = props

    const { useTagsQuery } = useFiltersApi()

    const query = useTagsQuery(params)
    const { data, isLoading, isError, isFetched, isFetching } = query
    const labels = useFilterLabels()
    const { t } = useTranslation('translations')

    const optionMap = React.useMemo<Map<string, Tag>>(() => {
        if (!data) return new Map<string, Tag>()
        return new Map(data.map((tag) => [tag.tagId.toString(), tag]))
    }, [data])

    const options = data?.map((tag) => {
        return {
            value: tag.tagId.toString(),
            label: tag.tagName,
        }
    })

    const renderRichLabel = React.useCallback(
        (
            option: Option,
            highlightMatchingText?: (input: string) => string | JSX.Element
        ) => {
            if (typeof option.value !== 'string') return option.label

            const rawOption = optionMap.get(option.value)

            if (rawOption) {
                return (
                    <div>
                        {highlightMatchingText?.(rawOption.tagName) ??
                            rawOption.tagName}
                    </div>
                )
            }

            return option.label
        },
        [optionMap]
    )

    const passedProps: ComponentProps<typeof SelectFilter> = {
        name,
        disabled: !!disabled,
        onChange: onChange,
        config: {
            value: value,
            options: options,
        },
        searchConfig: {
            name: `${name}-search`,
            placeholder: t('filters.tag.searchPlaceholder'),
        },
        value: value,
        defaultValue: defaultValue,
        renderRichLabel,
        label: t(`filters.tag.label`),
        ...labels,
        isLoading: isLoading || isFetching || !isFetched,
        isError,
        valueLabel: props.valueLabel,
        containerRef: props.containerRef,
        className: props.className,
    }

    return <SelectFilter {...passedProps} />
}
